import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Form, Button, Row, Col, Alert, Spinner } from "react-bootstrap";
import { FaTrash, FaPlus, FaArrowLeft } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { AiOutlineClose } from "react-icons/ai";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const AddEditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [product, setProduct] = useState({
    name: "",
    code: "",
    category: "",
    remarks: "",
    productDescription: "",
    status: true,
    modelGroups: [
      {
        id: Date.now(), // Add unique ID
        modelCode: "",
        modelName: "",
        modelDescription: "",
        lotSize: "",
        mfgPricePerLot: "",
        images: [],
        existingImages: [],
        video: null,
        existingVideo: null,
        variants: [{ variantName: "", variantValues: [] }],
      },
    ],
  });

  const [categories, setCategories] = useState([]);
  const [variantNameOptions, setVariantNameOptions] = useState([]);
  const [variantValueOptions, setVariantValueOptions] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");
  const animatedComponents = makeAnimated();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [categoriesRes, variantNamesRes, variantValuesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/categories`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_BASE_URL}/api/variant-names`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_BASE_URL}/api/variant-values`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        setCategories(categoriesRes.data);
        setVariantNameOptions(variantNamesRes.data);
        setVariantValueOptions(variantValuesRes.data);

        if (isEditMode) {
          const productRes = await axios.get(`${API_BASE_URL}/api/products/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const productData = productRes.data;
          const transformedModelGroups = productData.modelGroups.map(group => ({
            ...group,
            id: group._id || Date.now(), // Ensure each group has an ID
            variants: group.variants.map(variant => ({
              variantName: variant.variantName?._id || variant.variantName || "",
              variantValues: Array.isArray(variant.variantValues)
                ? variant.variantValues.map(val => val._id || val)
                : []
            })),
            existingImages: group.images || [],
            existingVideo: group.video || null,
            images: [],
            video: null
          }));

          setProduct({
            ...productData,
            category: productData.category?._id || productData.category || "",
            modelGroups: transformedModelGroups,
            existingImages: [],
            existingVideo: null,
            images: [],
            video: null
          });
        }
      } catch (err) {
        console.error(err);
        setErrorMessage("Failed to load required data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditMode, token]);

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image", "video"],
      ["clean"]
    ]
  };

  const formats = [
    "header",
    "bold", "italic", "underline", "strike",
    "color", "background",
    "list", "bullet",
    "link", "image", "video"
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProduct((prev) => ({ ...prev, [name]: value }));
  };

  const handleModelGroupFileChange = (groupIndex, field, files) => {
    const updatedModelGroups = [...product.modelGroups];
    const maxImageSize = 200 * 1024;
    const maxVideoSize = 500 * 1024;

    if (field === "images") {
      const validImages = Array.from(files).filter(file => {
        if (file.size > maxImageSize) {
          alert(`Image "${file.name}" exceeds 200KB limit and cannot be uploaded.`);
          return false;
        }
        return true;
      });
      updatedModelGroups[groupIndex].images = [
        ...updatedModelGroups[groupIndex].images,
        ...validImages
      ];
    } else if (field === "video") {
      const videoFile = files[0];
      if (videoFile && videoFile.size > maxVideoSize) {
        alert(`Video "${videoFile.name}" exceeds 500KB limit and cannot be uploaded.`);
        return;
      }
      updatedModelGroups[groupIndex].video = videoFile;
    }

    setProduct(prev => ({ ...prev, modelGroups: updatedModelGroups }));
  };

  const handleModelGroupChange = (index, field, value) => {
    const updated = [...product.modelGroups];
    updated[index][field] = value;
    setProduct((prev) => ({ ...prev, modelGroups: updated }));
  };

  const handleAddModelGroup = () => {
    const newGroup = {
      id: Date.now(), // Unique ID for the new group
      modelCode: "",
      modelName: "",
      modelDescription: "",
      lotSize: "",
      mfgPricePerLot: "",
      images: [],
      existingImages: [],
      video: null,
      existingVideo: null,
      variants: [{ variantName: "", variantValues: [] }],
    };

    setProduct((prev) => ({
      ...prev,
      modelGroups: [newGroup, ...prev.modelGroups], // Prepend instead of append
    }));

    // Scroll to the newly added group
    setTimeout(() => {
      const formElement = document.querySelector('.card-body');
      if (formElement) {
        formElement.scrollTop = formElement.scrollHeight;
      }
    }, 100);
  };

  const handleDeleteModelGroup = (index) => {
    if (product.modelGroups.length > 1) {
      const updated = [...product.modelGroups];
      updated.splice(index, 1);
      setProduct((prev) => ({ ...prev, modelGroups: updated }));
    }
  };

  const handleAddVariantPair = (groupIndex) => {
    const updated = [...product.modelGroups];
    updated[groupIndex].variants.push({ variantName: "", variantValues: [] });
    setProduct((prev) => ({ ...prev, modelGroups: updated }));
  };

  const handleDeleteVariantPair = (groupIndex, variantIndex) => {
    const updated = [...product.modelGroups];
    if (updated[groupIndex].variants.length > 1) {
      updated[groupIndex].variants.splice(variantIndex, 1);
      setProduct((prev) => ({ ...prev, modelGroups: updated }));
    }
  };

  const handleVariantNameChange = (groupIndex, variantIndex, selectedOption) => {
    const updated = [...product.modelGroups];
    updated[groupIndex].variants[variantIndex].variantName = selectedOption.value;
    updated[groupIndex].variants[variantIndex].variantValues = [];
    setProduct((prev) => ({ ...prev, modelGroups: updated }));
  };

  const handleVariantValuesChange = (groupIndex, variantIndex, selectedOptions) => {
    const updated = [...product.modelGroups];
    updated[groupIndex].variants[variantIndex].variantValues = selectedOptions.map(opt => opt.value);
    setProduct((prev) => ({ ...prev, modelGroups: updated }));
  };

  const getFilteredValues = (variantNameId) => {
    if (!variantNameId) return [];

    const variantValueDoc = variantValueOptions.find(v =>
      v.variantName === variantNameId || v.variantName?._id === variantNameId
    );

    if (!variantValueDoc) return [];

    const values = variantValueDoc.values || [];
    return values.map(value => ({
      value: value,
      label: value
    }));
  };

  const getSelectedValues = (groupIndex, variantIndex) => {
    const selectedValues = product.modelGroups[groupIndex].variants[variantIndex].variantValues;
    if (!selectedValues || selectedValues.length === 0) return [];

    return selectedValues.map(value => ({
      value: value,
      label: value
    }));
  };

  const removeModelGroupImage = (groupIndex, index, isExisting = false) => {
    const updatedModelGroups = [...product.modelGroups];

    if (isExisting) {
      updatedModelGroups[groupIndex].existingImages.splice(index, 1);
    } else {
      updatedModelGroups[groupIndex].images.splice(index, 1);
    }

    setProduct(prev => ({ ...prev, modelGroups: updatedModelGroups }));
  };

  const removeModelGroupVideo = (groupIndex, isExisting = false) => {
    const updatedModelGroups = [...product.modelGroups];

    if (isExisting) {
      updatedModelGroups[groupIndex].existingVideo = null;
    } else {
      updatedModelGroups[groupIndex].video = null;
    }

    setProduct(prev => ({ ...prev, modelGroups: updatedModelGroups }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");
    setLoading(true);

    try {
      const formData = new FormData();
      const {
        name,
        code,
        category,
        remarks,
        productDescription,
        status,
        modelGroups,
      } = product;

      formData.append("name", name);
      formData.append("code", code);
      formData.append("category", category);
      formData.append("remarks", remarks);
      formData.append("productDescription", productDescription);
      formData.append("status", status);

      const serializedGroups = modelGroups.map(group => ({
        modelCode: group.modelCode,
        modelName: group.modelName,
        modelDescription: group.modelDescription,
        lotSize: group.lotSize,
        mfgPricePerLot: group.mfgPricePerLot,
        existingImages: group.existingImages || [],
        existingVideo: group.existingVideo || null,
        variants: group.variants.map(variant => ({
          variantName: variant.variantName,
          variantValues: variant.variantValues,
        })),
      }));

      formData.append("modelGroups", JSON.stringify(serializedGroups));

      modelGroups.forEach((group, groupIndex) => {
        if (group.images && group.images.length > 0) {
          group.images.forEach((file) => {
            if (file instanceof File) {
              formData.append(`modelGroups[${groupIndex}][images]`, file);
            }
          });
        }

        if (group.video && group.video instanceof File) {
          formData.append(`modelGroups[${groupIndex}][video]`, group.video);
        }
      });

      const url = isEditMode
        ? `${API_BASE_URL}/api/products/${id}`
        : `${API_BASE_URL}/api/products`;

      const method = isEditMode ? "put" : "post";

      await axios[method](url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      setSuccessMessage(`Product ${isEditMode ? "updated" : "created"} successfully!`);

      if (!isEditMode) {
        setProduct({
          name: "",
          code: "",
          category: "",
          remarks: "",
          productDescription: "",
          status: true,
          modelGroups: [
            {
              id: Date.now(),
              modelCode: "",
              modelName: "",
              modelDescription: "",
              lotSize: "",
              mfgPricePerLot: "",
              images: [],
              existingImages: [],
              video: null,
              existingVideo: null,
              variants: [{ variantName: "", variantValues: [] }],
            },
          ],
        });
      }

      setTimeout(() => {
        navigate("/Product");
      }, 1000);
    } catch (err) {
      console.error(err);
      setErrorMessage(`Failed to ${isEditMode ? "update" : "create"} product.`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading product data...</span>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="card shadow">
        <div className="card-header text-white d-flex justify-content-between align-items-center"
          style={{ backgroundColor: '#102234' }}>
          <h5 className="mb-0">{isEditMode ? "Edit Product" : "Add Product"}</h5>
          <Button variant="light" size="sm" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" />
            Back
          </Button>
        </div>

        <div className="card-body">
          {successMessage && <Alert variant="success" className="mb-4">{successMessage}</Alert>}
          {errorMessage && <Alert variant="danger" className="mb-4">{errorMessage}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    name="name"
                    value={product.name}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Code *</Form.Label>
                  <Form.Control
                    name="code"
                    value={product.code}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    name="category"
                    value={product.category}
                    onChange={handleChange}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    name="remarks"
                    value={product.remarks}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label>Product Description</Form.Label>
              <ReactQuill
                value={product.productDescription}
                onChange={(val) => setProduct(prev => ({ ...prev, productDescription: val }))}
                modules={modules}
                formats={formats}
                style={{ height: "200px", marginBottom: "50px" }}
              />
            </Form.Group>

            <hr
              className="my-4"
              style={{
                borderColor: '#102234',
                borderWidth: '2px',
                backgroundColor: '#102234',
                height: '2px',
                opacity: 1,
                borderRadius: '2px',
                boxShadow: '0 2px 4px rgba(0, 128, 255, 0.2)'
              }}
            />
            <div className="d-flex justify-content-between align-items-center mb-3">

              <h5 className="mb-0">Model Groups</h5>
              <Button variant="primary" size="sm" onClick={handleAddModelGroup} style={{ width: '170px', backgroundColor: '#102234' }}>
                <FaPlus className="me-1" /> Add Model Group
              </Button>
            </div>

            {product.modelGroups.map((group, gi) => (
              <div
                key={group.id}
                className="p-3 rounded mb-4 position-relative shadow-sm"
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #102234",
                  boxShadow: "0 2px 6px rgba(16, 34, 52, 0.1)"
                }}
              >
                {product.modelGroups.length > 1 && (
                  <Button
                    variant="danger"
                    size="sm"
                    className="position-absolute top-0 end-0 mt-2 me-2"
                    onClick={() => handleDeleteModelGroup(gi)}
                  >
                    <FaTrash />
                  </Button>
                )}

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Model Name *</Form.Label>
                      <Form.Control
                        value={group.modelName}
                        onChange={(e) => handleModelGroupChange(gi, "modelName", e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Model Code *</Form.Label>
                      <Form.Control
                        value={group.modelCode}
                        onChange={(e) => handleModelGroupChange(gi, "modelCode", e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <ReactQuill
                    value={group.modelDescription}
                    onChange={(val) => handleModelGroupChange(gi, "modelDescription", val)}
                    modules={modules}
                    formats={formats}
                    style={{ height: "150px", marginBottom: "50px" }}
                  />
                </Form.Group>

                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Variant Pairs</h6>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    style={{ backgroundColor: '#102234', color: 'white' }}
                    onClick={() => handleAddVariantPair(gi)}
                  >
                    <FaPlus /> Add Variant
                  </Button>
                </div>

                {group.variants.map((variant, vi) => (
                  <div key={vi} className="mb-3 p-3 border rounded" style={{ backgroundColor: "#fff" }}>
                    <Row>
                      <Col md={5}>
                        <Form.Group className="mb-3">
                          <Form.Label>Variant Name *</Form.Label>
                          <Select
                            options={variantNameOptions.map(vn => ({
                              value: vn._id,
                              label: vn.name
                            }))}
                            value={variantNameOptions.find(vn => vn._id === variant.variantName) ?
                              {
                                value: variant.variantName,
                                label: variantNameOptions.find(vn => vn._id === variant.variantName).name
                              } : null}
                            onChange={(selected) => handleVariantNameChange(gi, vi, selected)}
                            placeholder="Select Variant Name"
                            isClearable
                          />
                        </Form.Group>
                      </Col>
                      <Col md={5}>
                        <Form.Group className="mb-3">
                          <Form.Label>Variant Values *</Form.Label>
                          <Select
                            isMulti
                            components={animatedComponents}
                            options={getFilteredValues(variant.variantName)}
                            value={getSelectedValues(gi, vi)}
                            onChange={(selected) => handleVariantValuesChange(gi, vi, selected)}
                            isDisabled={!variant.variantName}
                            placeholder="Select Variant Values"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2} className="d-flex align-items-end">
                        {group.variants.length > 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="w-100 mb-3 py-2"
                            onClick={() => handleDeleteVariantPair(gi, vi)}
                            aria-label="Delete Variant Pair"
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </div>
                ))}

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Lot Size *</Form.Label>
                      <Form.Control
                        type="number"
                        value={group.lotSize}
                        onChange={(e) => handleModelGroupChange(gi, "lotSize", e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>MFG Price Per Lot *</Form.Label>
                      <Form.Control
                        type="number"
                        value={group.mfgPricePerLot}
                        onChange={(e) => handleModelGroupChange(gi, "mfgPricePerLot", e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Images (Max 200KB each) *</Form.Label>
                      <Form.Control
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) => handleModelGroupFileChange(gi, "images", e.target.files)}
                      />
                    </Form.Group>

                    {isEditMode && group.existingImages.length > 0 && (
                      <div className="mt-3">
                        <h6>Existing Images</h6>
                        <div className="d-flex flex-wrap gap-2">
                          {group.existingImages.map((imgUrl, idx) => (
                            <div key={`existing-${gi}-${idx}`} className="position-relative" style={{ width: "100px", height: "100px" }}>
                              <img
                                src={`${API_BASE_URL}/${imgUrl}`}
                                alt="preview"
                                className="img-thumbnail w-100 h-100 object-fit-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removeModelGroupImage(gi, idx, true)}
                                className="position-absolute top-0 end-0 bg-danger text-white border-0 rounded-circle p-1"
                                style={{ transform: "translate(30%, -30%)" }}
                                aria-label="Remove image"
                              >
                                <AiOutlineClose size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {group.images.length > 0 && (
                      <div className="mt-3">
                        <h6>New Images</h6>
                        <div className="d-flex flex-wrap gap-2">
                          {group.images.map((imgFile, idx) => {
                            const imgUrl = URL.createObjectURL(imgFile);
                            return (
                              <div key={`new-${gi}-${idx}`} className="position-relative" style={{ width: "100px", height: "100px" }}>
                                <img
                                  src={imgUrl}
                                  alt="preview"
                                  className="img-thumbnail w-100 h-100 object-fit-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeModelGroupImage(gi, idx)}
                                  className="position-absolute top-0 end-0 bg-danger text-white border-0 rounded-circle p-1"
                                  style={{ transform: "translate(30%, -30%)" }}
                                  aria-label="Remove image"
                                >
                                  <AiOutlineClose size={14} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Video (Max 500KB)</Form.Label>
                      <Form.Control
                        type="file"
                        accept="video/*"
                        onChange={(e) => handleModelGroupFileChange(gi, "video", e.target.files)}
                      />
                    </Form.Group>

                    {isEditMode && group.existingVideo && (
                      <div className="mt-3">
                        <h6>Existing Video</h6>
                        <div className="position-relative" style={{ maxWidth: "300px" }}>
                          <video
                            controls
                            src={`${API_BASE_URL}/${group.existingVideo}`}
                            className="w-100"
                          />
                          <button
                            type="button"
                            onClick={() => removeModelGroupVideo(gi, true)}
                            className="position-absolute top-0 end-0 bg-danger text-white border-0 rounded-circle p-1"
                            style={{ transform: "translate(30%, -30%)" }}
                            aria-label="Remove video"
                          >
                            <AiOutlineClose size={16} />
                          </button>
                        </div>
                      </div>
                    )}

                    {group.video && (
                      <div className="mt-3">
                        <h6>New Video</h6>
                        <div className="position-relative" style={{ maxWidth: "300px" }}>
                          <video
                            controls
                            src={URL.createObjectURL(group.video)}
                            className="w-100"
                          />
                          <button
                            type="button"
                            onClick={() => removeModelGroupVideo(gi)}
                            className="position-absolute top-0 end-0 bg-danger text-white border-0 rounded-circle p-1"
                            style={{ transform: "translate(30%, -30%)" }}
                            aria-label="Remove video"
                          >
                            <AiOutlineClose size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>
              </div>
            ))}

            <Form.Group className="mb-4">
              <Form.Check
                type="switch"
                id="status"
                label={product.status ? "Active" : "Inactive"}
                checked={product.status}
                onChange={() => setProduct((prev) => ({ ...prev, status: !prev.status }))}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => navigate('/Product')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="success"
                style={{ width: "140px" }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {isEditMode ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  isEditMode ? "Update Product" : "Create Product"
                )}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default AddEditProduct;